import { useEffect, useMemo } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import {
  getTenantIdFromHash,
  isValidSession,
  setSigninJumperUrlPath,
} from '@services/session'
import { useIdleLogout } from '@hooks/useAuthGuard'

/**
 * Sysadmin URL params that, when present on any incoming URL (including
 * a protected route), mean "this is a sysadmin assume entry point". The
 * guard forwards them through to `/login` as top-level query params so
 * the LoginPage's `isSysAdminFlow` detection picks them up — they must
 * NOT be wrapped inside `?targetUrl=…` (which would double-encode them
 * and hide the trigger).
 */
const SYSADMIN_PARAM_KEYS = [
  'sysadmintoken',
  'sysadmintenantid',
  'sysadminusername',
  'purpose',
] as const

/**
 * Protected-routes gate. Mirrors process-runner's
 * `renderProtectedRouterView`: when no valid session is present,
 * capture `pathname + search + hash` into `signinJumperUrlPath`
 * (session storage) AND attach the same value as `?targetUrl=…` on the
 * redirect to `/login`. The `#tid_${tenantId}` hash, if present, is
 * preserved on the redirect URL too so the login form pre-fills the
 * tenant.
 *
 * Special case — **sysadmin assume**: when the incoming URL carries
 * `sysadmintoken` + `sysadmintenantid`, those params (plus
 * `sysadminusername` + `purpose`) are forwarded through to `/login` as
 * top-level query params so LoginPage's sysadmin handler fires. Any
 * other query params + the original pathname/hash are still recorded
 * into `targetUrl` so the user lands back on the correct post-login
 * destination.
 *
 * Precedence on the login side: `signinJumperUrlPath > targetUrl > /`.
 */
export function AuthGuard() {
  const location = useLocation()
  const valid = isValidSession()
  useIdleLogout()

  const fullUrl = `${location.pathname}${location.search}${location.hash}`

  // Detect a sysadmin entry-point. Sysadmin links always route through
  // LoginPage so an existing session can show the tenant/user switch
  // dialog before the assume request clears local session state.
  const sysAdmin = useMemo(() => {
    const sp = new URLSearchParams(location.search)
    const token = getSearchParam(sp, 'sysadmintoken')
    const tid = getSearchParam(sp, 'sysadmintenantid')
    if (!token || !tid) return null
    const params = new URLSearchParams()
    SYSADMIN_PARAM_KEYS.forEach((k) => {
      const v = getSearchParam(sp, k)
      if (v !== null) params.set(k, v)
    })
    // Capture any *other* query params + the original pathname/hash as
    // the post-login destination, so e.g. `/tasks/42?foo=bar` with a
    // sysadmin token still lands back on `/tasks/42?foo=bar`.
    const remaining = new URLSearchParams()
    sp.forEach((v, k) => {
      if (!SYSADMIN_PARAM_KEYS.includes(k.toLowerCase() as (typeof SYSADMIN_PARAM_KEYS)[number])) {
        remaining.set(k, v)
      }
    })
    const targetSearch = remaining.toString()
    const targetUrl =
      location.pathname === '/login'
        ? ''
        : `${location.pathname}${targetSearch ? `?${targetSearch}` : ''}${location.hash}`
    if (targetUrl && targetUrl !== '/') params.set('targetUrl', targetUrl)
    return params
  }, [location.search, location.pathname, location.hash])

  useEffect(() => {
    // Don't stash `signinJumperUrlPath` for sysadmin entries — the
    // sysadmin flow has its own targetUrl handling and the jumperUrl
    // would otherwise leak the sysadmintoken into session storage.
    if (!valid && fullUrl && location.pathname !== '/login' && !sysAdmin) {
      setSigninJumperUrlPath(fullUrl)
    }
  }, [valid, fullUrl, location.pathname, sysAdmin])

  // Build the redirect URL.
  const loginRedirect = useMemo(() => {
    if (sysAdmin) {
      const hashTid = getTenantIdFromHash(location.hash)
      const hash = hashTid ? `#tid_${hashTid}` : ''
      return `/login?${sysAdmin.toString()}${hash}`
    }
    if (valid) return ''
    const params = new URLSearchParams()
    if (fullUrl && location.pathname !== '/login') {
      params.set('targetUrl', fullUrl)
    }
    const hashTid = getTenantIdFromHash(location.hash)
    const hash = hashTid ? `#tid_${hashTid}` : ''
    const q = params.toString()
    return `/login${q ? `?${q}` : ''}${hash}`
  }, [valid, sysAdmin, fullUrl, location.hash, location.pathname])

  if (!valid || sysAdmin) {
    return <Navigate to={loginRedirect} replace />
  }
  return <Outlet />
}

export default AuthGuard

function getSearchParam(search: URLSearchParams, name: string): string | null {
  const direct = search.get(name)
  if (direct !== null) return direct

  const expected = name.toLowerCase()
  for (const [key, value] of search) {
    if (key.toLowerCase() === expected) return value
  }
  return null
}
